import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refreshToken'));

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const refreshAuthToken = useCallback(async () => {
    try {
      const response = await axios.post('/api/auth/refresh', {
        refreshToken: refreshToken
      });

      const { token: newToken, refreshToken: newRefreshToken } = response.data;
      
      setToken(newToken);
      setRefreshToken(newRefreshToken);
      localStorage.setItem('token', newToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      return newToken;
    } catch (error) {
      throw error;
    }
  }, [refreshToken]);

  // Check for existing authentication on app load
  useEffect(() => {
    const checkAuth = async () => {
      console.log('Checking auth with token:', token ? 'present' : 'missing');
      if (token) {
        try {
          const response = await axios.get('https://auth.ignaspanavas.com/api/users/profile');
          console.log('User profile fetched:', response.data);
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          // Try to refresh token
          if (refreshToken) {
            try {
              await refreshAuthToken();
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
              logout();
            }
          } else {
            logout();
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [refreshAuthToken, refreshToken, token]);

  const login = (userData, authToken, authRefreshToken) => {
    setUser(userData);
    setToken(authToken);
    setRefreshToken(authRefreshToken);
    localStorage.setItem('token', authToken);
    localStorage.setItem('refreshToken', authRefreshToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    toast.success('Welcome to your dashboard!');
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Logged out successfully');
  };

  const loginWithGoogle = () => {
    window.location.href = `https://auth.ignaspanavas.com/api/auth/google/login`;
  };

  const connectSocialAccount = (platform) => {
    // Redirect to backend OAuth endpoint
    window.location.href = `https://auth.ignaspanavas.com/api/auth/${platform}`;
  };

  const disconnectSocialAccount = async (accountId) => {
    try {
      await axios.delete(`https://auth.ignaspanavas.com/api/social/accounts/${accountId}`);
      toast.success('Account disconnected successfully');
      
      // Refresh user data to get updated account list
          const response = await axios.get('https://auth.ignaspanavas.com/api/users/profile');
      setUser(response.data);
    } catch (error) {
      console.error('Error disconnecting account:', error);
      toast.error('Failed to disconnect account');
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('https://auth.ignaspanavas.com/api/users/profile', profileData);
      setUser(response.data);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  const value = {
    user,
    loading,
    token,
    refreshToken,
    login,
    logout,
    loginWithGoogle,
    connectSocialAccount,
    disconnectSocialAccount,
    updateProfile,
    refreshAuthToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
